import React, { useState, useEffect } from "react";
import { getXenoAccountsData } from "../../../services/dashboard";
import { xenoAccounts as texts_es } from "../../../utils/text/graphTexts";
import { xenoAccounts as texts_en } from "../../../utils/text/graphTexts_en";
import {
  generateLineToolTip,
  generateMultiplLineTooltip,
} from "../../../utils/functions/generateLineTooltip";
import { useParams } from "react-router-dom";
import {
  COUNTRY_OPTIONS,
  getCountriesAsSelectOptions,
} from "../../../utils/constants/countryOptions";
import CardContainer from "../../reusable/CardContainer";
import CenterSpin from "../../misc/spin";
import CardOnlyYear from "../../reusable/CardOnlyYear";
import { getDateString } from "../../../utils/functions/getDateString";
import {
  getMonthlyXenophobicAccounts,
  getYearlyXenophobicAccounts,
} from "../../../services/singleMonths";
import { getPreviousMonth } from "../../../utils/functions/getPreviousMonth";
import {
  getSimpleLineConfig,
  GRID_CONFIG,
  GRID_CONFIG_WITH_LEGEND,
  TOOLTIP_STYLE,
} from "../../../utils/constants/graphConfigs";
import { handleYearChange } from "../../../utils/functions/handleYearChange";
import { seriesValues } from "../../../utils/functions/seriesValues";
import { transformNameCountries } from "../../../utils/functions/transformNameCountries";
import { useContextData } from "../../../hooks/context";
import { useCurrentLanguage } from "../../../hooks/useCurrentLanguage";
import CardError from "../../reusable/CardError/CardError";
import miscText from "../../../utils/text/misc.json";
import { getGraphFileName } from "../../../utils/functions/getGraphFileName";
import { isCountryIncluded } from "../../../utils/functions/isCountryIncluded";
import { getDownloadSubtitle } from "../../../utils/functions/getDownloadSubtitle";

export default function XenoAccounts({ selectedCountry, colorRBG }) {
  const { country } = useParams();
  const { dateOptions } = useContextData();
  const { language } = useCurrentLanguage();

  // State

  const [currentCountry, setCurrentCountry] = useState(
    selectedCountry ?? country
  );
  const [currentCardType] = useState("xeno-accounts");
  const [texts, setTexts] = useState(language === "en" ? texts_en : texts_es);
  const [countryProps, setCountryProps] = useState(
    COUNTRY_OPTIONS.find(
      (opt) => opt.code === selectedCountry || opt.code === country
    )
  );
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(true);
  const [errorLoadingGraph, setErrorLoadingGraph] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [options, setOptions] = useState();
  const [selectedYear, setSelectedYear] = useState();
  const [lastMonthData, setLastMonthData] = useState();
  const [isMostRecentYear, setIsMostRecentYear] = useState(true);

  // Country Select
  const [countrySelectConfig, setCountrySelectConfig] = useState({
    isIncluded: currentCountry === "LAC",
    value: ["LAC"],
    options: getCountriesAsSelectOptions(language),
  });

  // Unique to Tweets component
  // Never send unique states to Card components
  const [change, setChange] = useState();

  const createDateString = () => {
    let dateString;

    try {
      dateString = isMostRecentYear
        ? getDateString(
            language,
            dateOptions.mostRecentDate.year,
            dateOptions.mostRecentDate.month
          )
        : getDateString(
            language,
            selectedYear === "HIST"
              ? dateOptions.mostRecentDate.year
              : selectedYear
          );
    } catch (error) {
      dateString = "-----";
    }

    return dateString;
  };

  function handleCountrySelectChange(newValue) {
    setCountrySelectConfig({
      ...countrySelectConfig,
      value: newValue,
    });
  }

  // Useeffect that collents yead and month data
  useEffect(() => {
    if (dateOptions) {
      setSelectedYear("HIST");
      setIsLoadingInitialData(false);
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions]);

  // useEffect to load data for graph and creates Echarts options
  // Listend to dateOptions and selected year
  // Only funs when these params are not null
  useEffect(() => {
    async function updateData() {
      try {
        setIsLoading(true);

        const response = await getXenoAccountsData(
          currentCountry,
          selectedYear
        );

        if (response) {
          const transformedDatesData = transformNameCountries(
            response.data,
            language
          );

          let countries = Object.keys(transformedDatesData);
          countries.shift();

          let series = seriesValues(transformedDatesData);

          if (country === "LAC") {
            countries = countries.filter((c) =>
              isCountryIncluded(c, countrySelectConfig.value, language)
            );

            series = series.filter((line) =>
              isCountryIncluded(line.name, countrySelectConfig.value, language)
            );
          }

          const options = {
            ...getSimpleLineConfig(
              currentCountry,
              countries,
              colorRBG,
              transformedDatesData.dates,
              true,
              true,
              language
            ),
            grid:
              selectedCountry === "LAC" ? GRID_CONFIG_WITH_LEGEND : GRID_CONFIG,
            series: series,
            tooltip: {
              trigger: "axis",
              ...TOOLTIP_STYLE,
              formatter: function (params) {
                const isPercentage = true;
                const areColorsSwitched = false;
                if (currentCountry === "LAC") {
                  return generateMultiplLineTooltip(
                    params,
                    transformedDatesData,
                    isPercentage,
                    areColorsSwitched,
                    language
                  );
                } else {
                  return generateLineToolTip(
                    params,
                    "accounts",
                    selectedYear,
                    transformedDatesData,
                    dateOptions.yearOptions,
                    isPercentage,
                    language
                  );
                }
              },
            },
          };

          if (currentCountry !== "LAC") {
            options["legend"]["show"] = false;
            options["grid"]["right"] = "150px";
          } else {
            options["legend"]["show"] = true;
            options["grid"]["right"] = "150px";
          }

          setOptions(options);
          setIsDataAvailable(true);
          setIsLoading(false);
          setErrorLoadingGraph(false);
        }
      } catch (error) {
        setErrorLoadingGraph(true);
        setOptions(null);
        setIsDataAvailable(false);
        setIsLoading(false);
      }
    }

    if (!isLoadingInitialData && dateOptions && selectedYear) updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateOptions,
    selectedYear,
    isLoadingInitialData,
    currentCountry,
    countrySelectConfig,
  ]);

  // useEffect to load data for description dynamic data
  useEffect(() => {
    const getData = async () => {
      const isFirstYearSelected = selectedYear === dateOptions.firstYear;

      let promises, callback;
      if (isMostRecentYear) {
        const previousMonth = getPreviousMonth(
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month,
          dateOptions.yearOptions
        );

        const mostRecentMonthPromise = getMonthlyXenophobicAccounts(
          currentCountry,
          dateOptions.mostRecentDate.year,
          dateOptions.mostRecentDate.month
        );

        const previousMonthPromise = getMonthlyXenophobicAccounts(
          currentCountry,
          previousMonth.year,
          previousMonth.month
        );

        promises = [mostRecentMonthPromise, previousMonthPromise];
        callback = (results) => {
          const avgCurrentMonth = results[0].data.value;
          const avgPreviousMonth = results[1].data.value;

          const diff = avgCurrentMonth - avgPreviousMonth;
          setChange(diff.toFixed(1));
          setLastMonthData(avgCurrentMonth.toFixed(1));
        };
      } else if (isFirstYearSelected) {
        const yearXenoAccountsPromise = getYearlyXenophobicAccounts(
          currentCountry,
          selectedYear
        );

        promises = [yearXenoAccountsPromise];
        callback = (results) => {
          const avgCurrentYear = results.data.value;

          setChange("--");
          setLastMonthData(avgCurrentYear.toFixed(1));
        };
      } else {
        const currentYearPromise = getYearlyXenophobicAccounts(
          currentCountry,
          selectedYear
        );

        const previousYearPromise = getYearlyXenophobicAccounts(
          currentCountry,
          parseInt(selectedYear) - 1
        );

        promises = [currentYearPromise, previousYearPromise];
        callback = (results) => {
          const avgCurrentYear = results[0].data.value;
          const avgPreviousYear = results[1].data.value;

          const diff = avgCurrentYear - avgPreviousYear;
          setChange(diff.toFixed(1));
          setLastMonthData(avgCurrentYear.toFixed(1));
        };
      }
      Promise.all(promises)
        .then(callback)
        .catch((error) => {
          setChange("--");
          setLastMonthData("--");
        });
    };

    if (!isLoadingInitialData && dateOptions && selectedYear) getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateOptions, isLoadingInitialData, selectedYear, currentCountry]);

  useEffect(() => {
    if (selectedCountry) {
      setCurrentCountry(selectedCountry);
      setCountrySelectConfig({
        ...countrySelectConfig,
        isIncluded: selectedCountry === "LAC",
      });
      setCountryProps(
        COUNTRY_OPTIONS.find((opt) => opt.code === selectedCountry)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  useEffect(() => {
    setTexts(language === "en" ? texts_en : texts_es);
    setCountrySelectConfig({
      ...countrySelectConfig,
      options: getCountriesAsSelectOptions(language),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  function XenoAccountsContent() {
    let content;
    try {
      content = (
        <CardOnlyYear
          countryProps={countryProps}
          dateOptions={dateOptions}
          isLoading={isLoading}
          errorLoading={errorLoadingGraph}
          isDataAvailable={isDataAvailable}
          options={options}
          selectedYear={selectedYear}
          lastMonthData={lastMonthData ? lastMonthData + "%" : "--"}
          dataDate={createDateString()}
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description(
            isMostRecentYear,
            isMostRecentYear ? dateOptions.mostRecentDate.month : selectedYear,
            change
          )}
          selectArray={[
            {
              selectedValue: selectedYear,
              changeHandler: (newSelectedYear) =>
                handleYearChange(
                  newSelectedYear,
                  setIsMostRecentYear,
                  setSelectedYear,
                  dateOptions
                ),
              options: dateOptions.yearOptions,
              placeHolderText: miscText["yearPlaceholder"][language],
              labelAttributeName: "years_list",
              valueAttributeName: "years_code",
            },
          ]}
          language={language}
          includesCountrySelect={countrySelectConfig.isIncluded}
          handleCountrySelectChange={handleCountrySelectChange}
          countrySelectDefaultValue={countrySelectConfig.value}
          countrySelectOptions={countrySelectConfig.options}
          downloadableCardID={currentCardType}
          downloadableCardFileName={getGraphFileName(
            language === "es" ? "CuentasXenofobas" : "XenophobicAccounts",
            currentCountry,
            selectedYear
          )}
          downloadableCardTitle={texts.boxTitle}
          downloadableCardSubtitle={getDownloadSubtitle(
            countryProps,
            selectedYear,
            language
          )}
          downloadableCardContainsLegend={currentCountry === "LAC"}
        />
      );
    } catch (error) {
      content = (
        <CardError
          cardTitle={texts.boxTitle}
          methodology={texts.methodology}
          dataSubtitle={texts.dataSubtitle}
          description={texts.description()}
          selectArray={[
            {
              placeHolderText: miscText["yearPlaceholder"][language],
              language: language,
            },
          ]}
        />
      );
    }

    return content;
  }

  return (
    <CardContainer>
      {isLoadingInitialData ? <CenterSpin /> : <XenoAccountsContent />}
    </CardContainer>
  );
}
